/**
 * The backend quotes an installer's own refusal text as `Its own message: "<text>"`
 * (services/installer_gate_outcome.py); the quoted text never holds a double quote.
 */
const VENDOR_QUOTE = /Its own message: "[^"]*"/g

/**
 * The error without any quoted installer text, for matching our own wording only: a vendor
 * message that mentions an antivirus or a threat must not read as a scan verdict.
 */
export function withoutVendorQuote(message: string): string {
  return message.replace(VENDOR_QUOTE, 'Its own message: ""')
}

/** Internal exceptions whose text names the mechanism, and what a person reads instead. The
 *  backend applies the same table (workers/tasks.py `plain_internal_errors`). */
const INTERNAL_ERRORS: [RegExp, string][] = [
  [/\b(?:celery\.exceptions\.)?SoftTimeLimitExceeded\b(?:\(\))?/g, 'Packaging took too long and was stopped'],
  [/\b(?:celery\.exceptions\.)?TimeLimitExceeded\b(?:\([^)]*\))?/g, 'Packaging took too long and was stopped'],
  [/\bWorkerLostError\b(?:\([^)]*\))?(?::\s*)?/g, ''],
  [/\bWorker exited prematurely: signal \d+ \(\w+\)/g, 'The packaging worker stopped unexpectedly'],
]

/** A job error with each known internal exception replaced by its plain sentence. */
export function plainJobError(message: string): string {
  return INTERNAL_ERRORS.reduce((text, [pattern, plain]) => text.replace(pattern, plain), message)
}

/**
 * The one line worth showing for a failed job: the cause, not the log that led to it.
 *
 * A packaging failure arrives as the tail of the run — timestamped WARNING lines and a traceback,
 * joined with ' | ' and often duplicated — so the first hundred characters are retry noise and
 * never the reason. The deepest non-noise line is, which is what the list and the details heading
 * show; the full text stays available underneath.
 */
export function errorSummary(message: string, max = 160): string {
  const seen = new Set<string>()
  const parts = message
    .split(/\s*\|\s*|\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s && !seen.has(s) && seen.add(s))

  const strip = (s: string) =>
    s
      .replace(/^\[\d{1,2}:\d{2}(:\d{2})?\]\s*/, '')
      .replace(/^(WARNING|ERROR|INFO|DEBUG|CRITICAL)[:\s]\s*/i, '')
      .trim()

  // Retry chatter, the traceback header and its frames say what the code was doing, not what went
  // wrong.
  const isNoise = (s: string) =>
    /^\[?\d{1,2}:\d{2}[^\]]*\]?\s*(WARNING|DEBUG|INFO)\b/i.test(s) ||
    /^(WARNING|DEBUG|INFO)\b/i.test(s) ||
    /^Traceback\b/i.test(s) ||
    /^File ["']/.test(s) ||
    /^\s*at /.test(s)

  const candidates = parts.filter((s) => !isNoise(s)).map(strip).filter(Boolean)
  const line = plainJobError(
    candidates.length ? candidates[candidates.length - 1] : strip(parts[0] ?? message),
  )

  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line
}
